if (!customElements.get('product-shipping-calculator')) {
  const COUNTRY = 'Brazil';
  const CEP_LENGTH = 8;
  const REQUEST_TIMEOUT_MS = 8000;
  const MIN_CART_TOKEN_LENGTH = 32;

  const UF_TO_STATE = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
  };

  function sanitizeCep(value) {
    return String(value || '').replace(/\D/g, '').slice(0, CEP_LENGTH);
  }

  function formatCep(value) {
    const digits = sanitizeCep(value);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  function getCookie(name) {
    const parts = (`; ${document.cookie}`).split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return '';
  }

  function setCartCookie(value) {
    const expires = new Date(Date.now() + 14 * 86400000).toUTCString();
    document.cookie = `cart=${value}; expires=${expires}; path=/`;
  }

  async function fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      return response;
    } finally {
      window.clearTimeout(timer);
    }
  }

  function normalizeViaCepShape(data) {
    if (!data || data.erro) {
      throw new Error('CEP não encontrado.');
    }

    const uf = data.uf || '';
    const province = data.estado || UF_TO_STATE[uf] || uf;

    if (!province || !data.localidade) {
      throw new Error('Endereço incompleto para este CEP.');
    }

    return {
      zip: sanitizeCep(data.cep) || data.cep,
      province,
      city: data.localidade,
      country: COUNTRY,
    };
  }

  async function fetchViaCep(cep) {
    const response = await fetchWithTimeout(`https://viacep.com.br/ws/${cep}/json/`);
    if (!response.ok) {
      throw new Error('ViaCEP indisponível.');
    }

    const data = await response.json();
    return normalizeViaCepShape(data);
  }

  async function fetchBuscarCep(cep) {
    const response = await fetchWithTimeout(`https://opencep.com/v1/${cep}.json`);
    if (!response.ok) {
      throw new Error('Busca CEP indisponível.');
    }

    const data = await response.json();
    return normalizeViaCepShape(data);
  }

  async function lookupAddress(cep) {
    try {
      return await fetchViaCep(cep);
    } catch (primaryError) {
      try {
        return await fetchBuscarCep(cep);
      } catch (fallbackError) {
        throw primaryError;
      }
    }
  }

  function formatShippingPrice(price, currency) {
    const numericPrice = Number.parseFloat(price);

    if (Number.isNaN(numericPrice) || numericPrice === 0) {
      return 'Grátis';
    }

    if (currency === 'BRL') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(numericPrice);
    }

    return `R$ ${String(price).replace('.', ',')}`;
  }

  function formatDeliveryDays(deliveryDays) {
    if (!Array.isArray(deliveryDays) || deliveryDays.length === 0) {
      return '';
    }

    const days = Number.parseInt(deliveryDays[0], 10);
    if (Number.isNaN(days) || days <= 0) {
      return '';
    }

    return days === 1 ? '1 dia útil' : `${days} dias úteis`;
  }

  function buildRatesHtml(rates) {
    if (!rates.length) {
      return '<p class="wt-product__shipping-calculator__empty">Nenhuma opção de frete disponível para este CEP.</p>';
    }

    const items = rates
      .map((rate) => {
        const price = formatShippingPrice(rate.price, rate.currency);
        const delivery = formatDeliveryDays(rate.delivery_days);

        return `
          <li class="wt-product__shipping-calculator__rate">
            <span class="wt-product__shipping-calculator__rate-name">${rate.name}</span>
            <span class="wt-product__shipping-calculator__rate-price">${price}</span>
            ${delivery ? `<span class="wt-product__shipping-calculator__rate-days">${delivery}</span>` : ''}
          </li>
        `;
      })
      .join('');

    return `<ul class="wt-product__shipping-calculator__rates">${items}</ul>`;
  }

  async function addTemporaryCartItem(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: variantId,
        quantity,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || 'Não foi possível simular o carrinho para calcular o frete.');
    }

    return data;
  }

  async function fetchShippingRates(address) {
    const params = new URLSearchParams({
      'shipping_address[country]': address.country,
      'shipping_address[province]': address.province,
      'shipping_address[zip]': address.zip,
    });

    const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const message = Object.values(data || {}).join(' ') || 'Não foi possível calcular o frete.';
      throw new Error(message);
    }

    return data.shipping_rates || [];
  }

  async function withTemporaryCart(variantId, quantity, callback) {
    const originalCartToken = getCookie('cart');
    let workingToken = originalCartToken;

    if (!workingToken) {
      workingToken = `temp-cart-cookie___${Date.now()}`;
      setCartCookie(workingToken);
      workingToken = getCookie('cart');
    }

    if (!workingToken || workingToken.length < MIN_CART_TOKEN_LENGTH) {
      throw new Error('Não foi possível preparar o carrinho para calcular o frete.');
    }

    const fakeToken = `fake-cart-cookie___${Date.now()}`;
    setCartCookie(fakeToken);

    try {
      await addTemporaryCartItem(variantId, quantity);
      return await callback();
    } finally {
      setCartCookie(originalCartToken || workingToken);
    }
  }

  class ProductShippingCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-shipping-form]');
      this.cepInput = this.querySelector('[data-shipping-cep]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.results = this.querySelector('[data-shipping-results]');
      this.errors = this.querySelector('[data-shipping-errors]');
      this.sectionId = this.dataset.sectionId;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleCepInput = this.handleCepInput.bind(this);

      this.form?.addEventListener('submit', this.handleSubmit);
      this.cepInput?.addEventListener('input', this.handleCepInput);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.cepInput?.removeEventListener('input', this.handleCepInput);
    }

    handleCepInput() {
      if (!this.cepInput) return;
      this.cepInput.value = formatCep(this.cepInput.value);
    }

    getVariantId() {
      const productForm = document.querySelector(`#product-form-${this.sectionId}`);
      const variantInput = productForm?.querySelector('[name="id"]');
      return variantInput?.value || '';
    }

    getQuantity() {
      const quantityInput = document.querySelector('.js-counter-quantity');
      const quantity = Number.parseInt(quantityInput?.value, 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      const label = this.submitButton.querySelector('[data-shipping-submit-label]');
      if (label) {
        label.textContent = isLoading
          ? this.submitButton.dataset.loadingLabel || 'Calculando...'
          : this.submitButton.dataset.defaultLabel || 'Calcular';
      }
    }

    showResults(html) {
      if (!this.results || !this.errors) return;

      this.errors.hidden = true;
      this.errors.innerHTML = '';
      this.results.hidden = false;
      this.results.innerHTML = html;
    }

    showError(message) {
      if (!this.results || !this.errors) return;

      this.results.hidden = true;
      this.results.innerHTML = '';
      this.errors.hidden = false;
      this.errors.textContent = message;
    }

    clearMessages() {
      if (!this.results || !this.errors) return;

      this.results.hidden = true;
      this.results.innerHTML = '';
      this.errors.hidden = true;
      this.errors.textContent = '';
    }

    async handleSubmit(event) {
      event.preventDefault();

      const cep = sanitizeCep(this.cepInput?.value);
      const variantId = this.getVariantId();

      if (cep.length !== CEP_LENGTH) {
        this.showError('Informe um CEP válido com 8 dígitos.');
        return;
      }

      if (!variantId) {
        this.showError('Selecione uma variante do produto para calcular o frete.');
        return;
      }

      this.setLoading(true);
      this.clearMessages();

      try {
        const address = await lookupAddress(cep);
        address.zip = cep;

        const rates = await withTemporaryCart(variantId, this.getQuantity(), () =>
          fetchShippingRates(address)
        );

        this.showResults(buildRatesHtml(rates));
      } catch (error) {
        this.showError(error?.message || 'Não foi possível calcular o frete. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('product-shipping-calculator', ProductShippingCalculator);
}
